import { SetSelectionCommand } from "../commands/SetSelectionCommand";
import type { Layer, RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import { compositeLayer } from "../pixel/blend";
import { screenToDoc } from "../render/Viewport";
import { Selection, type SelectionBounds } from "../selection/Selection";
import type { PointerSample, Tool, ToolContext } from "./Tool";

// Quick Selection Tool — "paint" a selection by dragging the brush.
//
// Photoshop shortcut W (shared with Magic Wand). As you drag, the tool
// analyzes pixels within the brush radius and grows the selection into
// similar-colored regions using an 8-connected flood-fill approach.
//
// Modifier convention:
//   default      → replace (first stroke) / add (subsequent strokes)
//   shift        → add (union)
//   alt          → subtract
//   shift + alt  → intersect

type CombineMode = "replace" | "add" | "subtract" | "intersect";

export class QuickSelectionTool implements Tool {
  readonly id = "quick-selection";
  readonly shortcut = "W";
  brushSize = 20; // diameter in doc px
  tolerance = 32; // 0..255 max-channel
  /**
   * When true, region-growing samples the flattened composite of all visible
   * layers instead of only the active layer's raw pixels (Photoshop's
   * "Sample All Layers" option) — see buildDocumentComposite() (audit i160).
   */
  sampleAllLayers = false;
  /**
   * Photoshop's "Auto-Enhance": applies a light edge-refinement pass (a small
   * morphological smooth + sub-pixel feather) to reduce the blocky/jagged
   * look of the raw region-grow boundary (audit i161).
   */
  autoEnhance = false;
  /**
   * Explicit combine-mode override set by the options bar's New/Add/
   * Subtract/Intersect button strip (ToolUX's wireCombineModeButtons,
   * audit i60/i162). When set to "add"/"subtract"/"intersect" it wins over
   * the shift/alt/first-stroke-derived default computed per grow step;
   * "new" (or unset) falls back to the existing shift/alt modifier
   * behavior (first stroke replaces, subsequent strokes add).
   */
  combineMode?: "new" | "add" | "subtract" | "intersect";

  private active: {
    currentMask: Uint8Array;
    lastDocX: number;
    lastDocY: number;
    firstMove: boolean;
    /** Flattened doc-space composite, built once per stroke when sampleAllLayers is on. */
    composite: Uint8ClampedArray | null;
  } | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    const doc = ctx.doc;
    if (!doc || !doc.activeLayerId) return;
    const layer = findRaster(doc.layers, doc.activeLayerId);
    if (!layer || !layer.visible) return;

    const p = screenToDoc(ctx.viewport, s.x, s.y);

    const initialSel = doc.selection;
    const currentMask = initialSel
      ? new Uint8Array(initialSel.mask)
      : new Uint8Array(doc.width * doc.height);

    this.active = {
      currentMask,
      lastDocX: p.x,
      lastDocY: p.y,
      firstMove: true,
      composite: this.sampleAllLayers
        ? buildDocumentComposite(doc.layers, doc.width, doc.height)
        : null,
    };

    this.growAtPoint(ctx, p.x, p.y, s.shiftKey, s.altKey);
    this.emitSelection(ctx);
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active || !ctx.doc) return;

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const dx = p.x - this.active.lastDocX;
    const dy = p.y - this.active.lastDocY;
    const len = Math.hypot(dx, dy);
    const step = Math.max(1, this.brushSize / 4);
    const n = Math.max(1, Math.ceil(len / step));

    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const cx = this.active.lastDocX + dx * t;
      const cy = this.active.lastDocY + dy * t;
      this.growAtPoint(ctx, cx, cy, s.shiftKey, s.altKey);
    }

    this.active.lastDocX = p.x;
    this.active.lastDocY = p.y;
    this.emitSelection(ctx);
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active || !ctx.doc) {
      this.active = null;
      return;
    }

    const sel = this.finalizeSelection(ctx.doc.width, ctx.doc.height);
    this.active = null;
    ctx.emit(new SetSelectionCommand(sel));
  }

  /**
   * Build the committed/preview Selection from the current stroke mask,
   * applying the Auto-Enhance edge-refinement pass when enabled (audit i161).
   */
  private finalizeSelection(docW: number, docH: number): Selection | null {
    if (!this.active) return null;
    const bounds = computeBounds(this.active.currentMask, docW, docH);
    if (!bounds) return null;
    let sel = new Selection(docW, docH, new Uint8Array(this.active.currentMask), bounds);
    if (this.autoEnhance) {
      // Auto-Enhance: a light morphological smooth (closes small notches in
      // the jagged region-grow boundary) plus a sub-pixel feather, softening
      // the otherwise hard-edged (0/255-only) region-grow result.
      sel = sel.smooth(1).feather(0.5);
    }
    return sel;
  }

  private growAtPoint(
    ctx: ToolContext,
    cx: number,
    cy: number,
    shiftKey: boolean,
    altKey: boolean,
  ): void {
    const doc = ctx.doc;
    if (!doc || !this.active) return;

    const radius = this.brushSize / 2;
    const tol = this.tolerance;

    if (this.active.composite) {
      // Sample All Layers: grow against the flattened doc-space composite,
      // directly in document coordinates (layer offset = 0 since the
      // composite already spans the whole document).
      const docW = doc.width;
      const docH = doc.height;
      const dx0 = Math.floor(cx);
      const dy0 = Math.floor(cy);
      if (dx0 < 0 || dy0 < 0 || dx0 >= docW || dy0 >= docH) return;

      const composite = this.active.composite;
      const seedIdx = (dy0 * docW + dx0) * 4;
      const sA = composite[seedIdx + 3] ?? 0;
      if (sA === 0) return;
      const sR = composite[seedIdx] ?? 0;
      const sG = composite[seedIdx + 1] ?? 0;
      const sB = composite[seedIdx + 2] ?? 0;

      // Only consume firstMove (replace → add transition) once we actually
      // reach a valid, non-transparent seed — matches the per-layer path
      // below and the pre-existing behavior (audit i160 must not regress
      // the existing first-stroke-replaces / subsequent-strokes-add rule).
      const combine = pickCombine(shiftKey, altKey, this.active.firstMove, this.combineMode);
      this.active.firstMove = false;

      const stampMask = regionGrow(
        composite, docW, docH, dx0, dy0, radius, sR, sG, sB, tol,
        this.active.currentMask, docW, 0, 0, combine,
      );
      if (!stampMask) return;

      const mask = this.active.currentMask;
      for (let i = 0; i < mask.length; i++) {
        const sv = stampMask[i] ?? 0;
        if (sv === 0) continue;
        if (combine === "subtract") {
          mask[i] = 0;
        } else if (sv > (mask[i] ?? 0)) {
          mask[i] = sv;
        }
      }
      return;
    }

    const layer = findRaster(doc.layers, doc.activeLayerId!);
    if (!layer || !layer.visible) return;

    const data = layer.pixels.data;
    const lw = layer.pixels.width;
    const lh = layer.pixels.height;

    const lx = Math.floor(cx - layer.x);
    const ly = Math.floor(cy - layer.y);
    if (lx < 0 || ly < 0 || lx >= lw || ly >= lh) return;

    const seedIdx = (ly * lw + lx) * 4;
    const sA = data[seedIdx + 3] ?? 0;
    if (sA === 0) return;
    const sR = data[seedIdx] ?? 0;
    const sG = data[seedIdx + 1] ?? 0;
    const sB = data[seedIdx + 2] ?? 0;

    const combine = pickCombine(shiftKey, altKey, this.active.firstMove, this.combineMode);
    this.active.firstMove = false;

    const stampMask = regionGrow(
      data, lw, lh, lx, ly, radius, sR, sG, sB, tol,
      this.active.currentMask, doc.width,
      layer.x, layer.y, combine,
    );

    if (!stampMask) return;

    const docW = doc.width;
    const mask = this.active.currentMask;
    const dx0 = Math.max(0, layer.x);
    const dy0 = Math.max(0, layer.y);
    const dx1 = Math.min(docW, layer.x + lw);
    const dy1 = Math.min(doc.height, layer.y + lh);

    for (let dy = dy0; dy < dy1; dy++) {
      const srcRow = (dy - layer.y) * lw;
      const dstRow = dy * docW;
      for (let dx = dx0; dx < dx1; dx++) {
        const sv = stampMask[srcRow + (dx - layer.x)] ?? 0;
        if (sv === 0) continue;
        const di = dstRow + dx;
        if (combine === "subtract") {
          mask[di] = 0;
        } else if (sv > (mask[di] ?? 0)) {
          mask[di] = sv;
        }
      }
    }
  }

  private emitSelection(ctx: ToolContext): void {
    const doc = ctx.doc;
    if (!doc || !this.active) return;
    const sel = this.finalizeSelection(doc.width, doc.height);
    ctx.emitTransient(new SetSelectionCommand(sel));
    ctx.requestRender();
  }
}

/**
 * Flatten every visible raster layer (bottom-to-top, per Document's layer
 * order convention — see Document.layers) into a single docW x docH RGBA8
 * buffer, honoring per-layer visibility/opacity/blend mode.
 *
 * Simplified composite: layer masks, clipping (`clippedToBelow`), blend-if
 * ranges, and adjustment layers are not applied. That's an intentional scope
 * limit — enough to make "Sample All Layers" actually consider colors from
 * non-active layers (previously a complete no-op; audit i160).
 */
function buildDocumentComposite(layers: readonly Layer[], docW: number, docH: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(docW * docH * 4);
  compositeLayersOnto(out, layers, docW, docH);
  return out;
}

function compositeLayersOnto(
  dst: Uint8ClampedArray,
  layers: readonly Layer[],
  docW: number,
  docH: number,
): void {
  for (const layer of layers) {
    if (!layer.visible) continue;
    if (layer.kind === "group") {
      compositeLayersOnto(dst, layer.children, docW, docH);
      continue;
    }
    if (layer.kind !== "raster") continue;
    const src = layerToDocRgba(layer, docW, docH);
    compositeLayer(dst, src, layer.blendMode, layer.opacity);
  }
}

function layerToDocRgba(layer: RasterLayer, docW: number, docH: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(docW * docH * 4);
  const { pixels } = layer;
  const lw = pixels.width;
  const lh = pixels.height;
  const lx = layer.x;
  const ly = layer.y;
  const x0 = Math.max(0, lx);
  const y0 = Math.max(0, ly);
  const x1 = Math.min(docW, lx + lw);
  const y1 = Math.min(docH, ly + lh);
  for (let dy = y0; dy < y1; dy++) {
    const srcRow = (dy - ly) * lw;
    const dstRow = dy * docW;
    for (let dx = x0; dx < x1; dx++) {
      const si = (srcRow + (dx - lx)) * 4;
      const di = (dstRow + dx) * 4;
      out[di] = pixels.data[si] ?? 0;
      out[di + 1] = pixels.data[si + 1] ?? 0;
      out[di + 2] = pixels.data[si + 2] ?? 0;
      out[di + 3] = pixels.data[si + 3] ?? 0;
    }
  }
  return out;
}

function pickCombine(shift: boolean, alt: boolean, isFirst: boolean, override?: string): CombineMode {
  // An explicit options-bar combine-mode selection (audit i60/i162) wins over
  // the shift/alt/first-stroke-derived default. "new" (or no override)
  // falls through to the existing modifier-key behavior below.
  if (override === "add" || override === "subtract" || override === "intersect") {
    return override;
  }
  if (shift && alt) return "intersect";
  if (shift) return "add";
  if (alt) return "subtract";
  return isFirst ? "replace" : "add";
}

/**
 * Region-growing flood fill within a circular brush radius.
 *
 * Stack-based (iterative) 8-connected flood fill. Grows from the seed
 * pixel into similar-colored neighbors within the brush circle.
 *
 * For "add" modes: returns a mask of newly selected pixels (skipping
 * pixels already in currentMask). Growth continues through already-
 * selected pixels to reach new areas.
 *
 * For "subtract" mode: returns a mask of selected pixels that match
 * the seed color (these will be deselected).
 */
function regionGrow(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  seedX: number,
  seedY: number,
  radius: number,
  sR: number,
  sG: number,
  sB: number,
  tol: number,
  currentMask: Uint8Array,
  docW: number,
  layerOffX: number,
  layerOffY: number,
  mode: CombineMode,
): Uint8Array | null {
  const n = width * height;
  const r2 = radius * radius;
  const subtract = mode === "subtract";

  // Seed pixel in layer coords.
  const sx = Math.max(0, Math.min(width - 1, seedX | 0));
  const sy = Math.max(0, Math.min(height - 1, seedY | 0));
  const seedPix = sy * width + sx;

  // Check seed color match.
  const si = seedPix * 4;
  const sA = pixels[si + 3] ?? 0;
  if (sA === 0) return null;
  const pr = pixels[si] ?? 0;
  const pg = pixels[si + 1] ?? 0;
  const pb = pixels[si + 2] ?? 0;
  const dR = pr > sR ? pr - sR : sR - pr;
  const dG = pg > sG ? pg - sG : sG - pg;
  const dB = pb > sB ? pb - sB : sB - pb;
  const seedDist = dR > dG ? (dR > dB ? dR : dB) : dG > dB ? dG : dB;
  if (seedDist > tol) return null;

  const seedDocIdx = (sy + layerOffY) * docW + (sx + layerOffX);
  const seedAlreadySel = (currentMask[seedDocIdx] ?? 0) > 0;

  // For subtract mode, seed must be already selected.
  if (subtract && !seedAlreadySel) return null;
  // For add mode with replace on first move, if seed is already selected
  // and there's an existing selection, we still grow from it.

  // visited tracks which pixels we've already processed.
  const visited = new Uint8Array(n);
  // mask collects the output (pixels to add or subtract).
  const mask = new Uint8Array(n);

  // Iterative stack.
  const stack = new Int32Array(n);
  let sp = 0;

  visited[seedPix] = 1;
  if (!subtract && !seedAlreadySel) {
    mask[seedPix] = 255;
  }
  stack[sp++] = seedPix;

  // 8-connected neighbor deltas.
  const d8 = [
    -1 - width, -width, 1 - width,
    -1,           1,
    -1 + width,  width, 1 + width,
  ];

  while (sp > 0) {
    const p = stack[--sp]!;
    const px = p % width;
    const py = (p / width) | 0;

    for (let ni = 0; ni < 8; ni++) {
      const np = p + d8[ni]!;
      if (np < 0 || np >= n) continue;
      if (visited[np]) continue;

      const npx = np % width;
      const npy = (np / width) | 0;

      // Check brush circle in doc space.
      const ndx = (npx + layerOffX + 0.5) - (seedX + layerOffX + 0.5);
      const ndy = (npy + layerOffY + 0.5) - (seedY + layerOffY + 0.5);
      if (ndx * ndx + ndy * ndy > r2) continue;

      visited[np] = 1;

      const i = np * 4;
      const a = pixels[i + 3] ?? 0;
      if (a === 0) continue;

      const r = pixels[i] ?? 0;
      const g = pixels[i + 1] ?? 0;
      const b = pixels[i + 2] ?? 0;

      const dr = r > sR ? r - sR : sR - r;
      const dg = g > sG ? g - sG : sG - g;
      const db = b > sB ? b - sB : sB - b;
      const dist = dr > dg ? (dr > db ? dr : db) : dg > db ? dg : db;

      if (dist <= tol) {
        const nDocIdx = (npy + layerOffY) * docW + (npx + layerOffX);
        const nAlreadySel = (currentMask[nDocIdx] ?? 0) > 0;

        if (subtract) {
          if (nAlreadySel) {
            mask[np] = 255;
            stack[sp++] = np;
          }
        } else {
          if (!nAlreadySel) {
            mask[np] = 255;
          }
          // Always continue growing from matching pixels.
          stack[sp++] = np;
        }
      }
    }
  }

  // Check if any pixels were marked.
  for (let i = 0; i < n; i++) {
    if (mask[i] !== 0) return mask;
  }
  return null;
}

function computeBounds(
  mask: Uint8Array,
  w: number,
  h: number,
): SelectionBounds | null {
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      if ((mask[row + x] ?? 0) !== 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}
